/**
 * The founder's full bid history (canon APP-010, CORE-025).
 *
 * The same three disciplines as every other seller read apply, and each is a canon rule
 * rather than a preference.
 *
 * **The session decides whose bids these are.** The request carries no parameter at all —
 * no seller id, no deal id, no date — so there is nothing a caller could vary to reach
 * another founder's history (`contracts/API.md` § Seller bid history shape). The
 * cross-seller guarantee here is an absence, not a check.
 *
 * **A response body is untrusted input.** Every field is parsed rather than cast, and a row
 * this app cannot read fails the panel instead of being skipped. A founder shown a short
 * history would reasonably conclude a bid was never placed — and the bid most likely to be
 * missing is the losing one this panel exists to explain.
 *
 * **The verdict is core's.** `outcome` is read, never derived: nothing here infers a loss
 * from a missing placement, a win from a rank, or a settled day from a date in the past.
 * Canon § 5 moves a founder's credit on that verdict, and a second opinion computed in a
 * browser could only contradict the ledger.
 */
import { apiRequest } from "./client";
import { INVALID, asRecord, malformed, nonEmptyString, optionalCount } from "./parse";
import type { SellerBid, SellerBidHistory, SellerBidOutcome, SellerBidPlacement } from "./types";

const BID_OUTCOMES = new Set<string>(["standing", "won", "lost", "withdrawn"]);

/**
 * An unrecognised outcome fails the parse instead of degrading to a default.
 *
 * There is no safe direction to guess in. Showing an unknown state as `lost` would tell a
 * founder they lost a day; showing it as `won` would tell them they hold a position they do
 * not have. Both are worse than "this didn't load", which is at least true.
 */
function isBidOutcome(value: string): value is SellerBidOutcome {
  return BID_OUTCOMES.has(value);
}

/**
 * What the bid bought, or `null` when it bought nothing.
 *
 * `null` is the contract's own value here — most bids buy nothing — so it is an answer
 * rather than an absence, and it is not confused with a placement that failed to parse. A
 * placement that *is* present must at least name itself: a row claiming a result it cannot
 * identify is not one this app may render.
 *
 * A rank of `0` is refused along with a negative one. Canon § 7 ranks from position 1, so
 * `0` is not a position core would send and not a position to render.
 */
function parseBidPlacement(value: unknown): SellerBidPlacement | null | typeof INVALID {
  if (value === null || value === undefined) return null;
  const row = asRecord(value);
  if (row === null) return INVALID;

  const placementId = nonEmptyString(row.placementId);
  if (placementId === null) return INVALID;

  const rank = optionalCount(row.rank);
  const winningBidCents = optionalCount(row.winningBidCents);
  if (rank === INVALID || winningBidCents === INVALID) return INVALID;
  if (rank !== null && rank < 1) return INVALID;

  return { placementId, rank, winningBidCents };
}

/** One bid row. Returns `null` when it is not one this app can render honestly. */
export function parseSellerBid(value: unknown): SellerBid | null {
  const row = asRecord(value);
  if (row === null) return null;

  const bidId = nonEmptyString(row.bidId);
  const displayDate = nonEmptyString(row.displayDate);
  const qualifiedAt = nonEmptyString(row.qualifiedAt);
  const outcome = nonEmptyString(row.outcome);
  const amountCents = optionalCount(row.amountCents);
  if (
    bidId === null ||
    displayDate === null ||
    qualifiedAt === null ||
    outcome === null ||
    !isBidOutcome(outcome) ||
    amountCents === INVALID ||
    amountCents === null
  ) {
    return null;
  }

  // Required as a real boolean rather than read for truthiness. Its *false* value is what
  // words a standing bid ("that day has not been decided"), so a missing field must not
  // arrive here as a confident `false`.
  if (typeof row.auctionFinalized !== "boolean") return null;

  const placement = parseBidPlacement(row.placement);
  if (placement === INVALID) return null;

  const deal = asRecord(row.deal);
  if (deal === null) return null;
  const id = nonEmptyString(deal.id);
  const slug = nonEmptyString(deal.slug);
  const productName = nonEmptyString(deal.productName);
  // A row that cannot name its deal cannot be rendered honestly: the founder would be told
  // that *something* of theirs was bid on, without being told what.
  if (id === null || slug === null || productName === null) return null;

  return {
    bidId,
    displayDate,
    amountCents,
    qualifiedAt,
    outcome,
    auctionFinalized: row.auctionFinalized,
    placement,
    deal: { id, slug, productName },
  };
}

/** Returns `null` when the body is not a bid history. */
export function parseSellerBids(body: unknown): SellerBidHistory | null {
  const row = asRecord(body);
  if (row === null) return null;

  const serverTime = nonEmptyString(row.serverTime);
  const timeZone = nonEmptyString(row.timeZone);
  if (serverTime === null || timeZone === null) return null;
  // Canon § 8 fixes Dealbid's day to UTC, and every date on this panel is formatted in UTC.
  // A body claiming another zone would put a founder's bid on the wrong day, so it is
  // refused rather than silently formatted as if it had said UTC.
  if (timeZone !== "UTC") return null;

  if (!Array.isArray(row.bids)) return null;
  const bids: SellerBid[] = [];
  for (const entry of row.bids) {
    const bid = parseSellerBid(entry);
    if (bid === null) return null;
    bids.push(bid);
  }

  return { bids, timeZone, serverTime };
}

/**
 * Every bid the signed-in founder has placed, newest display day first.
 *
 * The order is core's and is preserved as received. It is a total order (display date, then
 * amount, then qualifying time, then bid id), so two reads answer in the same sequence and a
 * founder does not see their history rearrange itself between visits.
 */
export async function fetchSellerBids(): Promise<SellerBidHistory> {
  const history = parseSellerBids(await apiRequest<unknown>("/v1/seller/bids"));
  if (history === null) throw malformed();
  return history;
}
